import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Input, Tabs, message, Checkbox } from 'antd';
import { createStyles } from 'antd-style';
import { UserOutlined, LockOutlined, PhoneOutlined } from '@ant-design/icons';
import { useAuth } from './context/AuthContext';
import { api } from './utils/api';

const useStyles = createStyles(({ token }) => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'auto',
      backgroundImage:
        "url('https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/V-_oS6r-i7wAAAAAAAAAAAAAFl94AQBr')",
      backgroundSize: '100% 100%',
    },
    content: {
      flex: '1',
      padding: '32px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    form: {
      width: '328px',
      maxWidth: '75vw',
      padding: '32px',
      backgroundColor: 'white',
      borderRadius: token.borderRadius,
      boxShadow: token.boxShadow,
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px',
    },
    logo: {
      height: '44px',
      marginBottom: '16px',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    subtitle: {
      color: token.colorTextSecondary,
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '24px',
    },
    footerBar: {
      textAlign: 'center',
      padding: '16px',
      color: token.colorTextSecondary,
    },
  };
});

const LoginMessage: React.FC<{
  content: string;
}> = ({ content }) => {
  return (
    <Alert
      style={{
        marginBottom: 24,
      }}
      message={content}
      type="error"
      showIcon
    />
  );
};

const Login: React.FC<{
  onLoginSuccess?: () => void;
}> = ({ onLoginSuccess }) => {
  const [loginStatus, setLoginStatus] = useState<string | undefined>(undefined);
  const [loginType, setLoginType] = useState<string>('account');
  const [loading, setLoading] = useState(false);
  const [refererName, setRefererName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>(''); // 存储具体的错误信息
  const [registerForm] = Form.useForm();
  const { styles } = useStyles();
  const { login } = useAuth();

  // 从 URL 中读取 referer_name 参数
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referer = urlParams.get('referer_name');
    if (referer) {
      setRefererName(referer);
      setLoginType('register'); // 自动切换到注册页面
      // 设置表单的推荐人手机号，显示为"手机号(推荐人)"格式
      registerForm.setFieldsValue({ referer_name: `${referer}(推荐人)` });
    }
  }, [registerForm]);

  // 处理登录
  const handleLogin = async (values: any) => {
    setLoading(true);
    setLoginStatus(undefined);
    
    try {
      const data = await api.login({
        username: values.username,
        password: values.password,
      });
      
      if (data.status === 200 && data.data) {
        // 登录成功
        login(data.data.token, data.data.user);
        message.success('登录成功！');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        // 登录失败
        setLoginStatus('error');
        message.error(data.msg || '登录失败');
      }
    } catch (error) {
      console.error('登录请求失败:', error);
      setLoginStatus('error');
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理注册
  const handleRegister = async (values: any) => {
    setLoading(true);
    setLoginStatus(undefined);
    setErrorMessage('');

    try {
      // 提取实际的推荐人手机号（去除"(推荐人)"后缀）
      let refererPhone = values.referer_name;
      if (refererPhone && refererPhone.endsWith('(推荐人)')) {
        refererPhone = refererPhone.replace('(推荐人)', '');
      }

      const data = await api.register({
        username: values.username,
        password: values.password,
        password2: values.password2,
        referer_name: refererPhone,
      });
      
      if (data.status === 200 && data.data) {
        // 注册成功，自动登录
        login(data.data.token, data.data.user);
        message.success('注册成功！');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        // 注册失败，显示具体错误信息
        setLoginStatus('error');
        // 根据错误码显示不同的错误提示
        if (data.status === 10002) {
          // 用户已存在
          setErrorMessage('该手机号已被注册，请直接登录或使用其他手机号');
        } else if (data.status === 10005) {
          // 两次密码不一致
          setErrorMessage('两次输入的密码不一致，请重新输入');
        } else if (data.status === 40001 || data.status === 400) {
          // 参数错误
          setErrorMessage('输入信息格式不正确，请检查手机号和密码格式');
        } else {
          // 其他错误，显示后端返回的错误消息
          setErrorMessage(data.msg || '注册失败，请检查输入信息');
        }
      }
    } catch (error) {
      console.error('注册请求失败:', error);
      setLoginStatus('error');
      setErrorMessage('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理忘记密码点击
  const handleForgotPassword = () => {
    alert('测试阶段未添加身份验证模块，请电话联系李医生18680495180验证并重置密码');
  };

  // 处理修改密码
  const handleChangePassword = async (values: any) => {
    setLoading(true);
    setLoginStatus(undefined);

    try {
      const data = await api.changePassword({
        username: values.username,
        oldpassword: values.oldpassword,
        newpassword: values.newpassword,
        newpassword2: values.newpassword2,
      });
      
      if (data.status === 200) {
        // 修改密码成功
        message.success('密码修改成功！请使用新密码登录');
        setLoginType('account'); // 切换回登录页面
      } else {
        // 修改密码失败
        setLoginStatus('error');
        message.error(data.msg || '修改密码失败');
      }
    } catch (error) {
      console.error('修改密码请求失败:', error);
      setLoginStatus('error');
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.form}>
          <div className={styles.header}>
            <img src="./dakanglogo.png" alt="logo" className={styles.logo} />
            <div className={styles.title}>达康AI医生</div>
          </div>

          <Tabs
            activeKey={loginType}
            onChange={(activeKey) => {
              setLoginType(activeKey);
              setLoginStatus(undefined);
              setErrorMessage(''); // 切换标签时清空错误信息
            }}
            centered
            items={[
              {
                key: 'account',
                label: '账户密码登录',
              },
              {
                key: 'changepassword',
                label: '修改密码',
              },
              {
                key: 'register',
                label: '新用户注册',
              },
            ]}
          />

          {loginStatus === 'error' && loginType === 'account' && (
            <LoginMessage content="登录失败，请检查手机号和密码" />
          )}

          {loginStatus === 'error' && loginType === 'register' && (
            <LoginMessage content={errorMessage || "注册失败，请检查输入信息"} />
          )}

          {loginStatus === 'error' && loginType === 'changepassword' && (
            <LoginMessage content="修改密码失败，请检查输入信息" />
          )}

          {loginType === 'account' && (
            <Form
              name="login"
              onFinish={handleLogin}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入手机号!' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="手机号" size="large" />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码！' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>

              <div className={styles.footer}>
                <a onClick={handleForgotPassword} style={{ cursor: 'pointer' }}>忘记密码</a>
              </div>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  登录
                </Button>
              </Form.Item>
            </Form>
          )}

          {loginType === 'register' && (
            <Form
              name="register"
              form={registerForm}
              onFinish={handleRegister}
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '请输入手机号!' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号！' }
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="手机号" size="large" />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码！' },
                  { min: 1, max: 16, message: '密码长度为1-16个字符!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="password2"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码！' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致！'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="确认密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="referer_name"
                rules={[
                  { 
                    pattern: /^(1[3-9]\d{9})(\(推荐人\))?$/, 
                    message: '请输入有效的推荐人手机号！' 
                  }
                ]}
              >
                <Input 
                  prefix={<PhoneOutlined />} 
                  placeholder="推荐人手机号（选填）" 
                  size="large"
                  disabled={!!refererName}
                />
              </Form.Item>
              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[{ required: true, message: '请阅读并同意用户使用协议' }]}
                style={{ marginBottom: 0 }}
              >
                <Checkbox>
                  我已阅读并同意
                  <a href="/user-agreement" target="_blank" rel="noopener noreferrer">《用户使用协议》</a>
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="privacy"
                valuePropName="checked"
                rules={[{ required: true, message: '请阅读并同意平台隐私政策' }]}
                style={{ marginBottom: 0 }}
              >
                <Checkbox>
                  我已阅读并同意
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">《平台隐私政策》</a>
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  注册
                </Button>
              </Form.Item>
            </Form>
          )}

          {loginType === 'changepassword' && (
            <Form
              name="changepassword"
              onFinish={handleChangePassword}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入手机号!' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="手机号" size="large" />
              </Form.Item>
              <Form.Item
                name="oldpassword"
                rules={[{ required: true, message: '请输入旧密码！' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="旧密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="newpassword"
                rules={[
                  { required: true, message: '请输入新密码！' },
                  { min: 1, max: 16, message: '密码长度为1-16个字符!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="新密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="newpassword2"
                dependencies={['newpassword']}
                rules={[
                  { required: true, message: '请确认新密码！' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newpassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的新密码不一致！'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="确认新密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  修改密码
                </Button>
              </Form.Item>
            </Form>
          )}
        </div>
      </div>
      <div className={styles.footerBar}>
        达康AI医生 ©2025
      </div>
    </div>
  );
};

export default Login; 